import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Select, Tag } from 'antd'
import { useIntl } from 'react-intl'

import styles from './styles.module.scss'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const allowedKeyDown = ['Enter', 'Tab', ',']

const MultipleEmailInput = ({ label, name, rules = [], newUi, suffixLabel, ...props }) => {
  const [inputValue, setInputValue] = useState('')

  const selectRef = useRef(null)

  const { formatMessage } = useIntl()
  const form = Form.useFormInstance()
  const values = Form.useWatch(name, form)

  useEffect(() => {
    if (newUi && values?.length > 0 && selectRef.current && props.placeholder) {
      const input = selectRef.current.nativeElement?.querySelector?.('input')
      if (input) {
        input.setAttribute('placeholder', props.placeholder)
      }
    }
  }, [values, props.placeholder, newUi])

  const _rules = useMemo(() => {
    return [
      ...rules,
      {
        validator: (_, value) => {
          if (!value || value.length === 0) {
            return Promise.resolve()
          }

          const invalidEmails = value.filter((email) => !emailRegex.test(email))
          if (invalidEmails.length > 0) {
            return Promise.reject(new Error(`Email tidak valid: ${invalidEmails.join(', ')}`))
          }

          return Promise.resolve()
        },
      },
    ]
  }, [rules])

  const handleInputKeyDown = useCallback(
    (e) => {
      if (allowedKeyDown.includes(e.key)) {
        const trimmedInputValue = typeof inputValue === 'string' ? inputValue.trim() : inputValue
        e.preventDefault()
        if (trimmedInputValue && emailRegex.test(trimmedInputValue)) {
          const currentValue = form.getFieldValue(name) || []
          if (!currentValue.includes(trimmedInputValue)) {
            form.setFields([
              {
                name,
                value: [...currentValue, trimmedInputValue],
                errors: [],
              },
            ])
          }
          setInputValue('')
        }
      }
    },
    [form, inputValue, name],
  )

  const handleBlur = useCallback(
    (e) => {
      e.preventDefault()
      const currentValue = form.getFieldValue(name) || []

      // Filter hanya email yang valid
      const validEmails = currentValue.filter((email) => emailRegex.test(email))

      // Jika input terakhir valid dan belum ada di list, tambahkan
      const trimmedInputValue = typeof inputValue === 'string' ? inputValue.trim() : inputValue
      if (
        trimmedInputValue &&
        emailRegex.test(trimmedInputValue) &&
        !validEmails.includes(trimmedInputValue)
      ) {
        validEmails.push(trimmedInputValue)
      }

      // Update value form dengan hanya email yang valid dan unik
      form.setFields([
        {
          name,
          value: validEmails,
          errors: [],
        },
      ])
      setInputValue('')
    },
    [form, inputValue, name],
  )

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault()
      const pastedText = e.clipboardData.getData('text')

      // Split by comma, semicolon, colon, space, newline, tab, or pipe and trim each email
      const emails = pastedText.split(/[,;:\s\n\t|]+/).map((email) => email.trim())

      // Filter valid emails
      const validEmails = emails.filter((email) => emailRegex.test(email))

      if (validEmails.length > 0) {
        const currentValue = form.getFieldValue(name) || []
        const newEmails = validEmails.filter((email) => !currentValue.includes(email))

        if (newEmails.length > 0) {
          form.setFields([
            {
              name,
              value: [...currentValue, ...newEmails],
              errors: [],
            },
          ])
        }
      }

      setInputValue('')
    },
    [form, name],
  )

  const handleChange = useCallback(
    (nextValue) => {
      const previous = form.getFieldValue(name) || []
      const current = Array.isArray(nextValue) ? nextValue : []

      const normalized = current
        .map((email) => (typeof email === 'string' ? email.trim() : email))
        .filter((email) => typeof email === 'string' && email.length > 0)

      const { validUnique, invalid } = normalized.reduce(
        (acc, email) => {
          if (emailRegex.test(email)) {
            if (!acc.validUnique.includes(email)) {
              acc.validUnique.push(email)
            }
          } else {
            acc.invalid.push(email)
          }
          return acc
        },
        { validUnique: [], invalid: [] },
      )

      if (invalid.length > 0) {
        form.setFields([
          {
            name,
            value: validUnique,
            errors: [formatMessage({ id: 'financeContacts.invalid_email_address_format' })],
          },
        ])
        return
      }

      form.setFields([
        {
          name,
          value: validUnique,
          errors: [],
        },
      ])

      // Clear search input only when a new valid tag is added
      if (Array.isArray(previous) && validUnique.length > previous.length) {
        setInputValue('')
      }
    },
    [form, formatMessage, name],
  )

  const tagRender = useCallback(
    ({ label: _label, closable, onClose }) => {
      const onPreventMouseDown = (event) => {
        event.preventDefault()
        event.stopPropagation()
      }

      return (
        <Tag
          color="blue"
          onMouseDown={onPreventMouseDown}
          closable={closable}
          onClose={onClose}
          style={{ marginInlineEnd: !newUi ? 4 : undefined, fontSize: 12 }}
          className={newUi ? styles.tag : undefined}
        >
          {_label}
        </Tag>
      )
    },
    [newUi],
  )

  const isRequired = useMemo(() => _rules.some((rule) => rule.required), [_rules])

  if (newUi) {
    return (
      <div>
        {label && (
          <div className={styles.label}>
            <label htmlFor={name}>
              {isRequired && <span style={{ color: '#e53e3e' }}>*</span>} {label}
            </label>
            {suffixLabel}
          </div>
        )}
        <Form.Item name={name} noStyle rules={_rules}>
          <Select
            ref={selectRef}
            suffixIcon={null}
            showSearch
            open={false}
            mode="tags"
            style={{ width: '100%' }}
            searchValue={inputValue}
            onSearch={setInputValue}
            onChange={handleChange}
            onInputKeyDown={handleInputKeyDown}
            onBlur={handleBlur}
            onPaste={handlePaste}
            allowClear
            tagRender={tagRender}
            className={styles.select}
            {...props}
          />
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {() => {
            const nameErrors = form.getFieldError(name)
            const error = nameErrors?.[0]
            return error ? <div className={styles.errorMessage}>{error}</div> : null
          }}
        </Form.Item>
      </div>
    )
  }

  return (
    <Form.Item name={name} label={label} rules={_rules}>
      <Select
        ref={selectRef}
        suffixIcon={null}
        showSearch
        open={false}
        mode="tags"
        style={{ width: '100%' }}
        searchValue={inputValue}
        onSearch={setInputValue}
        onChange={handleChange}
        onInputKeyDown={handleInputKeyDown}
        onBlur={handleBlur}
        onPaste={handlePaste}
        allowClear
        tagRender={tagRender}
        {...props}
      />
    </Form.Item>
  )
}

export default MultipleEmailInput
